/**
 * PlatformerSceneUi — the scene's UI shell, split out of
 * PlatformerScene.js (file-size rule) along the presentation seam.
 *
 * Everything here is chrome AROUND the simulation: the HUD text row,
 * the SceneChrome back/help/leaderboard bar, the dev-only "Stage
 * Editor" shortcut, and the end-of-run overlays (game over, level
 * complete, score submit). Each function takes the live scene and
 * reads/writes its fields directly — this module is a private half
 * of the scene, not a public API.
 */

import { Container, Text, TextStyle } from '../../../vendor/pixi.min.mjs';
import { GameOverOverlay } from '../../ui/GameOverOverlay.js';
import { LevelCompleteOverlay } from '../../ui/LevelCompleteOverlay.js';
import { ScoreSubmitOverlay } from '../../ui/ScoreSubmitOverlay.js';
import { SceneChrome } from '../../ui/SceneChrome.js';
import { paintChip } from '../../ui/shared.js';
import { partyMode } from '../../ui/Confetti.js';
import { t } from '../../config/i18n.js';
import { PLATFORMER_SCORING } from './platformerConfig.js';

// ── HUD ───────────────────────────────────────────────────────────

export function buildHud(scene) {
    scene._hudContainer.removeChildren();
    const padX = 24;
    const padY = 16;
    const style = new TextStyle({
        fontFamily: 'Poppins, Arial, sans-serif',
        fontSize: 28, fontWeight: '800', fill: '#fbbf24',
        stroke: { color: 0x111827, width: 4 },
    });
    const muted = new TextStyle({ ...style, fill: '#ffffff' });

    scene._hudLives = new Text({ text: '', style });
    scene._hudLives.x = padX;
    scene._hudLives.y = padY;
    scene._hudContainer.addChild(scene._hudLives);

    scene._hudCoins = new Text({ text: '', style });
    scene._hudCoins.x = padX + 220;
    scene._hudCoins.y = padY;
    scene._hudContainer.addChild(scene._hudCoins);

    scene._hudScore = new Text({ text: '', style: muted });
    scene._hudScore.x = scene.game.width / 2 - 110;
    scene._hudScore.y = padY;
    scene._hudContainer.addChild(scene._hudScore);

    scene._hudTime = new Text({ text: '', style: muted });
    scene._hudTime.x = scene.game.width / 2 + 200;
    scene._hudTime.y = padY;
    scene._hudContainer.addChild(scene._hudTime);
}

export function refreshHud(scene) {
    if (!scene._hudLives) return;
    scene._hudLives.text = `${t('common.stat_lives')}  ×${scene._lives}`;
    scene._hudCoins.text = `${t('common.stat_coins')}  ${String(scene._coins).padStart(2, '0')}`;
    scene._hudScore.text = `${t('common.stat_score')}  ${scene._score}`;
    const seconds = Math.floor(scene._timeFrames / 60);
    const mm = String(Math.floor(seconds / 60)).padStart(1, '0');
    const ss = String(seconds % 60).padStart(2, '0');
    scene._hudTime.text = `${t('common.stat_time')}  ${mm}:${ss}`;
}

export function formatTime(scene) {
    const seconds = Math.floor(scene._timeFrames / 60);
    const mm = String(Math.floor(seconds / 60)).padStart(1, '0');
    const ss = String(seconds % 60).padStart(2, '0');
    return `${mm}:${ss}`;
}

// ── Chrome (back button, help, leaderboard) ───────────────────────

export function buildChrome(scene) {
    scene._chrome = new SceneChrome({
        game: scene.game,
        host: scene._hudContainer,
        overlayHost: scene.container,
        gameId: 'platformer',
        getHelp: () => ({
            title: 'Platformer — How to Play',
            sections: [
                { heading: 'Move',  body: 'Arrow keys or WASD walk left/right. Hold Shift to run.' },
                { heading: 'Jump',  body: 'Space, W, or Up Arrow jump. Hold for higher jumps.' },
                { heading: 'Goal',  body: 'Reach the flag at the end of the level.' },
                { heading: 'Care',  body: 'Avoid Goombas + Koopas — stomp them from above.' },
                { heading: 'Power', body: 'Mushroom = grow Super (one free hit). ?-blocks drop coins / mushrooms.' },
            ],
        }),
        onBack: () => {
            // Editor → game test-play uses `editorReturnScene`
            // so Back returns to the editor, not the menu. Same
            // pattern TrainSim uses.
            const ret = scene.game.editorReturnScene;
            if (scene.game.fromEditor && ret) {
                scene.game.fromEditor = false;
                scene.game.editorReturnScene = null;
                scene.game.sceneManager.switchScene(ret);
            } else {
                scene.game.sceneManager.switchScene('menu');
            }
        },
    });
    scene._chrome.attach();
    buildEditorTestButton(scene);
}

/**
 * Dev-only "Stage Editor" shortcut pinned to the left of the Back
 * button so a designer can bounce straight from play-testing into
 * the editor. Stripped from production builds via the same
 * `__PRODUCTION_BUILD__` flag the editor scene itself is gated on,
 * so the button (and its handler) get dead-code eliminated there.
 */
export function buildEditorTestButton(scene) {
    if (globalThis.__PRODUCTION_BUILD__) return;

    const W = 240;
    const H = 70;
    // Back button: 300 wide, right edge at game.width-20 → its left
    // edge sits at game.width-320. Tuck this pill 14px to its left.
    const rightEdge = scene.game.width - 320 - 14;

    const btn = new Container();
    btn.eventMode = 'static';
    btn.cursor = 'pointer';

    const chip = paintChip({ w: W, h: H, r: 0, color: 0xfbbf24, shadowAlpha: 0 });
    chip.x = -W / 2;
    chip.y = -H / 2;
    btn.addChild(chip);

    const label = new Text({
        text: t('platformer.test_editor'),
        style: new TextStyle({
            fontFamily: 'Poppins, Arial, sans-serif',
            fontSize: 20, fontWeight: '900', fill: '#3b1670',
            letterSpacing: 1.0, align: 'center',
        }),
    });
    label.anchor.set(0.5);
    btn.addChild(label);

    btn.x = rightEdge - W / 2;
    btn.y = 55;

    btn.on('pointertap', () => {
        try { localStorage.setItem('mini-games-editor', '1'); } catch (e) {}
        scene.game.editorReturnScene = 'platformer';
        scene.game.editorTargetGame = 'platformer';
        // Resume the editing session we test-played from (restore the
        // working stage), rather than reopening the game-selector launcher.
        scene.game.editorResume = true;
        scene.game.sceneManager.switchScene('stage-editor');
    });
    btn.on('pointerover', () => { btn.scale.set(1.03); });
    btn.on('pointerout',  () => { btn.scale.set(1.0);  });
    btn.on('pointerdown', () => { btn.scale.set(0.97); });
    btn.on('pointerup',   () => { btn.scale.set(1.03); });

    scene._hudContainer.addChild(btn);
    scene._editorTestBtn = btn;
}

// ── End-of-run overlays ───────────────────────────────────────────

export function showGameOver(scene) {
    if (scene._gameOverOverlay) return;
    scene._gameOver = true;
    const overlay = new GameOverOverlay();
    overlay.show(scene.game.width, scene.game.height, {
        title: t('common.game_over'),
        won: false,
        stats: [
            { label: t('common.stat_score'), value: String(scene._score) },
            { label: t('common.stat_coins'), value: String(scene._coins) },
            { label: t('common.stat_time'),  value: formatTime(scene) },
        ],
        actions: [
            { label: t('common.retry'), primary: true,
              onClick: () => scene._restartFromGameOver() },
            { label: t('common.main_menu'), onClick: () => {
                scene._cleanupOverlays();
                scene.game.sceneManager.switchScene('menu');
            } },
        ],
    });
    scene._gameOverOverlay = overlay;
}

export function showWin(scene) {
    if (scene._won) return;
    scene._won = true;
    scene._score += PLATFORMER_SCORING.pointsPerFlag;
    try { partyMode(scene.game); } catch (err) {}
    const overlay = new LevelCompleteOverlay();
    overlay.show(scene.game.width, scene.game.height, {
        title: t('common.you_win'),
        lines: [
            `${t('common.stat_coins')}: ${scene._coins}`,
            `${t('common.stat_time')}:  ${formatTime(scene)}`,
        ],
        score: scene._score,
        scoreLabel: t('common.total_score'),
        onContinue: () => afterWin(scene),
    });
    scene._winOverlay = overlay;
    refreshHud(scene);
}

export function afterWin(scene) {
    scene._cleanupOverlays();
    const lbEnabled = scene.game.theme?.getSetting?.('shared', 'leaderboardsEnabled', false);
    if (!lbEnabled) {
        scene.game.sceneManager.switchScene('menu');
        return;
    }
    const submit = new ScoreSubmitOverlay();
    submit.show({
        game: 'platformer',
        score: scene._score,
        outcome: 'win',
        meta: { coins: scene._coins, timeFrames: scene._timeFrames },
        theme: scene.game.theme,
        onDone: () => {
            scene._scoreSubmitOverlay = null;
            scene.game.sceneManager.switchScene('menu');
        },
    });
    scene._scoreSubmitOverlay = submit;
}
